import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel/FormControlLabel";
import FormHelperText from "@mui/material/FormHelperText";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Typography from "@mui/material/Typography";
import {
  Control,
  Controller,
  FieldErrors,
  FieldValues,
  Path,
  PathValue,
} from "react-hook-form";

import { setControllerFieldRef } from "../../helpers/form";
import { getNestedProperty } from "../../helpers/object";
import {
  ValidationOptions,
  validationOptionsToControlRules,
} from "../../types/Validations";

export type RadioGroupOption = {
  label: string;
  id: string;
  belowField?: React.ReactNode;
};

interface APRadioGroupProps<T extends FieldValues> {
  name: Path<T>;
  label: string;
  options: RadioGroupOption[];
  validations: ValidationOptions;
  errors: FieldErrors<T>;
  control: Control<T>;
  defaultValue: PathValue<T, Path<T>> | undefined;
  showRequired?: boolean;
  hideRequired?: boolean;
  highlightOnEmpty?: boolean;
  readOnly?: boolean;
  hidden?: boolean;
}

const APRadioGroup = <T extends FieldValues>({
  name,
  label,
  options,
  errors,
  validations,
  control,
  defaultValue,
  showRequired,
  hideRequired,
  readOnly,
  highlightOnEmpty,
  hidden,
}: APRadioGroupProps<T>) => {
  const error = getNestedProperty(name, errors);

  return (
    <FormControl error={!!error}>
      <Controller<T>
        name={name}
        control={control}
        defaultValue={defaultValue}
        rules={validationOptionsToControlRules(label, validations)}
        render={({ field: { ref, ...field } }) => (
          <Box
            sx={{
              display: hidden ? "none" : undefined,
            }}
          >
            <Typography
              variant="titleMedium"
              color={
                !!error || (highlightOnEmpty && !field.value)
                  ? "error.main"
                  : "initial"
              }
            >
              {(validations.required || showRequired) && !hideRequired
                ? `${label} *`
                : label}
            </Typography>
            <RadioGroup sx={{ ml: 2, mt: 1 }} {...field}>
              {options.map((option, index) => {
                return (
                  <Box
                    key={option.id}
                    sx={{ display: "flex", flexDirection: "column" }}
                  >
                    <FormControlLabel
                      key={option.id}
                      value={option.id}
                      control={
                        <Radio
                          inputRef={
                            index === 0
                              ? (el) => setControllerFieldRef(ref, el)
                              : undefined
                          }
                        />
                      }
                      label={option.label}
                      disabled={readOnly}
                    />

                    {field.value === option.id && option.belowField && (
                      <Box my={1}>{option.belowField}</Box>
                    )}
                  </Box>
                );
              })}
            </RadioGroup>
          </Box>
        )}
      />
      {!!error && <FormHelperText>{`${error?.message}`}</FormHelperText>}
    </FormControl>
  );
};

export default APRadioGroup;
